import React, { useCallback, useEffect, useState } from 'react';
import { Plug, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { Panel, StatusPill } from '@/components/enterprise/ui';

function Row({ it, onChanged }) {
  const [baseUrl, setBaseUrl] = useState(it.base_url || '');
  const [token, setToken] = useState('');
  const [mode, setMode] = useState(it.mode || 'mock');
  const [enabled, setEnabled] = useState(it.enabled !== false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { mode, enabled, base_url: baseUrl };
      if (token) payload.token = token;
      await adminApi.patch(`/admin/integrations/${it.type}`, payload);
      setToken('');
      toast.success(`${it.name} gespeichert`);
      onChanged && onChanged();
    } catch (e) { toast.error('Speichern fehlgeschlagen'); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await adminApi.post(`/admin/integrations/${it.type}/test`);
      setTestResult(r.data);
      toast[r.data.ok ? 'success' : 'error'](`${it.name}: ${r.data.status}`);
    } catch (e) { setTestResult({ ok: false, status: 'error' }); toast.error('Test fehlgeschlagen'); } finally { setTesting(false); }
  };

  return (
    <div className="rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)] p-4" data-testid={`integration-${it.type}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2.5"><Plug className="h-4 w-4 text-[color:var(--itw-cyan)]" /><span className="text-sm font-medium text-[color:var(--itw-text)]">{it.name}</span><span className="text-[11px] font-mono text-[color:var(--itw-text-3)]">{it.category}</span></span>
        <StatusPill status={enabled ? 'connected' : 'offline'} label={enabled ? 'Aktiv' : 'Deaktiviert'} />
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="Base URL (z.B. https://pve.intern:8006)"
          className="h-9 rounded-lg bg-[color:var(--itw-surface)] border border-[color:var(--itw-border)] px-3 text-sm text-[color:var(--itw-text)] outline-none focus:border-[color:var(--itw-border-strong)]" data-testid={`integration-${it.type}-url`} />
        <input value={token} onChange={(e) => setToken(e.target.value)} type="password" placeholder={it.has_token ? '•••••••• (gespeichert)' : 'API-Key / Token'}
          className="h-9 rounded-lg bg-[color:var(--itw-surface)] border border-[color:var(--itw-border)] px-3 text-sm text-[color:var(--itw-text)] outline-none focus:border-[color:var(--itw-border-strong)]" data-testid={`integration-${it.type}-token`} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-[color:var(--itw-border)] overflow-hidden">
          {['mock', 'live'].map((m) => (
            <button key={m} onClick={() => setMode(m)} className={`px-3 py-1.5 text-xs font-mono ${mode === m ? 'bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)]' : 'text-[color:var(--itw-text-2)]'}`} data-testid={`integration-${it.type}-mode-${m}`}>{m}</button>
          ))}
        </div>
        <button onClick={() => setEnabled((v) => !v)} className={`px-3 py-1.5 text-xs rounded-lg border ${enabled ? 'border-[#2EF2A5]/40 text-[#2EF2A5]' : 'border-[color:var(--itw-border)] text-[color:var(--itw-text-3)]'}`}>{enabled ? 'Aktiviert' : 'Deaktiviert'}</button>
        <button onClick={test} disabled={testing} className="px-3 py-1.5 text-xs rounded-lg border border-[color:var(--itw-border)] text-[color:var(--itw-text-2)] hover:border-[color:var(--itw-border-strong)] inline-flex items-center gap-1.5" data-testid={`integration-${it.type}-test`}>
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (testResult ? (testResult.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#2EF2A5]" /> : <XCircle className="h-3.5 w-3.5 text-[#FF5C7A]" />) : null)} Verbindung testen
        </button>
        <button onClick={save} disabled={saving} className="ml-auto px-4 py-1.5 text-xs rounded-lg bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium" data-testid={`integration-${it.type}-save`}>{saving ? 'Speichern…' : 'Speichern'}</button>
      </div>
      {testResult && <div className="mt-2 text-[11px] font-mono text-[color:var(--itw-text-3)]">Ergebnis: status={testResult.status} · source={testResult.source || '—'} {testResult.error ? `· ${testResult.error}` : ''}</div>}
    </div>
  );
}

export default function IntegrationsPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { adminApi.get('/admin/integrations').then((r) => setItems(r.data.integrations || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Panel title="Integrationen" subtitle="connector registry · Mock ↔ Live · Verwaltung über die Oberfläche" testid="admin-integrations-panel">
      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">lade…</div>}
        {items.map((it) => <Row key={it.type} it={it} onChanged={load} />)}
      </div>
    </Panel>
  );
}
